import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Plus, Search, Trash2, MessageSquare, X, Upload, FileText, Users } from 'lucide-react';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription, Textarea, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, useToast } from '@hiring-intelligence/ui';
import { hiringApi, companyApi, ModuleType } from '@/services/api';
import { LocationSelect } from '@/components/LocationSelect';
import { ShareDialog } from '@/components/ShareDialog';
import { useAuthStore } from '@/stores/auth';
import { YEARS_OF_EXPERIENCE_LABELS, DIRECT_REPORTS_LABELS, GRADUATE_TYPES_LABELS, LANGUAGES, WORK_TYPES, CONTRACT_TYPE_LABELS, SERVICE_TYPE_OPTIONS, PRIORITY_OPTIONS, DEAL_STAGE_OPTIONS, formatNumber, parseFormattedNumber } from '@hiring-intelligence/shared';

// All available modules for generation (company_research is now at company level)
const ALL_MODULES: { type: ModuleType; label: string; description: string }[] = [
	{ type: 'deal_briefing', label: 'Deal Briefing', description: 'Unified meeting notes, team assessment & KPIs' },
	{ type: 'standard_jd', label: 'Standard JD', description: 'Public-facing job description' },
	{ type: 'blind_jd', label: 'Blind JD', description: 'Confidential JD without company info' }
];

// Module dependency: key depends on value (must be selected first)
const MODULE_DEPENDENCIES: Record<string, ModuleType[]> = {
	'deal_briefing': [],
	'standard_jd': ['deal_briefing'],
	'blind_jd': ['deal_briefing', 'standard_jd'],
	'candidate_sourcing': ['deal_briefing', 'standard_jd', 'blind_jd']
};

// Reverse: which modules depend on a given module (transitive)
const MODULE_DEPENDANTS: Record<string, ModuleType[]> = {
	'deal_briefing': ['standard_jd', 'blind_jd', 'candidate_sourcing'],
	'standard_jd': ['blind_jd', 'candidate_sourcing'],
	'blind_jd': ['candidate_sourcing'],
	'candidate_sourcing': []
};

export default function HiringRequestPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();
	const { toast } = useToast();
	const currentUser = useAuthStore((state) => state.user);
	const [searchParams] = useSearchParams();

	const [isCreating, setIsCreating] = useState(false);
	const [dealIdToLink, setDealIdToLink] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState(searchParams.get('company') || '');
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [shareTargetRequest, setShareTargetRequest] = useState<any>(null);
	const [creationStep, setCreationStep] = useState<'basic' | 'modules'>('basic');
	const [newRequest, setNewRequest] = useState({
		roleTitle: '',
		yearsOfExperience: 'below_2_years' as string,
		directReports: 'individual_contributor' as string,
		salaryRange: null as { min: number; max: number; currency: string } | null,
		languages: [] as string[],
		industry: [] as string[],
		graduates: '' as string,
		openToExpat: false,
		excludedCompanies: [] as string[],
		preferredCompanies: [] as string[],
		hiringManagerEmails: [] as Array<{ name: string; position?: string; linkedIn?: string }>,
		pic: [] as Array<{ contactId?: string; name: string; email: string }>,
		workType: 'hybrid' as string,
		contractType: '' as string,
		urgencyLevel: '' as string,
		serviceType: '' as string,
		priority: '' as string,
		feePercentage: '' as string,
		dealNotes: '' as string,
		targetDate: '' as string,
		companyId: '',
		location: ''
	});
	const [selectedModules, setSelectedModules] = useState<ModuleType[]>(ALL_MODULES.map(m => m.type));
	const [aiContext, setAiContext] = useState('');
	const [meetingNotes, setMeetingNotes] = useState('');
	const [contextFiles, setContextFiles] = useState<{ url: string; gcsPath: string; name: string; size: number; uploadedAt: string }[]>([]);
	const [isUploadingFile, setIsUploadingFile] = useState(false);

	const [page, setPage] = useState(1);
	const [creatorFilter, setCreatorFilter] = useState<string>(currentUser?.id || 'all');
	const [dealStatusFilter, setDealStatusFilter] = useState<string>('all');
	const PAGE_LIMIT = 10;

	// Debounce the search box so the server is queried at most once per typing pause.
	const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
		return () => clearTimeout(t);
	}, [searchQuery]);

	// Any change to the active filters resets pagination back to the first page.
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, creatorFilter, dealStatusFilter]);

	const { data: hiringData, isLoading } = useQuery({
		queryKey: ['hiring-requests', { page, limit: PAGE_LIMIT, search: debouncedSearch, creatorId: creatorFilter, dealStatus: dealStatusFilter }],
		queryFn: () => hiringApi.list(page, PAGE_LIMIT, { search: debouncedSearch, creatorId: creatorFilter, dealStatus: dealStatusFilter }),
		refetchOnWindowFocus: false,
		// Keep the current page visible while the next one loads (no "Loading..." flash on page change).
		placeholderData: keepPreviousData
	});

	// Full creator list for the filter dropdown — independent of the current page.
	const { data: creatorsData } = useQuery({
		queryKey: ['hiring-request-creators'],
		queryFn: () => hiringApi.listCreators(),
		refetchOnWindowFocus: false
	});

	const { data: companiesData } = useQuery({
		queryKey: ['companies'],
		queryFn: () => companyApi.list(),
		refetchOnWindowFocus: false
	});

	const { data: contactsData } = useQuery({
		queryKey: ['company-contacts', newRequest.companyId],
		queryFn: () => companyApi.listContacts(newRequest.companyId),
		enabled: !!newRequest.companyId,
		refetchOnWindowFocus: false
	});

	// Tag input helpers
	const [languageInput, setLanguageInput] = useState('');
	const [industryInput, setIndustryInput] = useState('');
	const [hmNameInput, setHmNameInput] = useState('');
	const [hmPositionInput, setHmPositionInput] = useState('');
	const [hmLinkedInInput, setHmLinkedInInput] = useState('');
	const [excludedInput, setExcludedInput] = useState('');
	const [preferredInput, setPreferredInput] = useState('');
	const [picInput, setPicInput] = useState({ name: '', email: '' });

	// Pre-fill + open the form when arriving from a deal's "Create new hiring request".
	useEffect(() => {
		const pf = (location.state as any)?.dealPrefill;
		if (!pf) return;
		setIsCreating(true);
		setCreationStep('basic');
		setNewRequest((prev) => ({
			...prev,
			roleTitle: pf.roleTitle || prev.roleTitle,
			companyId: pf.companyId || prev.companyId,
			pic: Array.isArray(pf.pic) ? pf.pic : prev.pic,
			// Show the values the deal will pass down, so the form isn't misleadingly blank.
			serviceType: pf.serviceType || prev.serviceType,
			priority: pf.priority || prev.priority,
			feePercentage: pf.feePercentage || prev.feePercentage,
			dealNotes: pf.dealNotes || prev.dealNotes
		}));
		if (pf.meetingNotes) setMeetingNotes(pf.meetingNotes);
		setDealIdToLink(pf.dealId || null);
		// Clear nav state so a back/refresh doesn't re-trigger the prefill.
		navigate(location.pathname, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const createMutation = useMutation({
		mutationFn: async (data: typeof newRequest) => {
			const { serviceType, priority, feePercentage, dealNotes, targetDate, ...rest } = data;
			const response = await hiringApi.create({
				...rest,
				serviceType: serviceType || undefined,
				priority: priority || undefined,
				feePercentage: feePercentage ? Number(feePercentage) : undefined,
				dealNotes: dealNotes || undefined,
				targetDate: targetDate ? new Date(targetDate).toISOString() : undefined,
				// Created from a deal → link server-side; otherwise the API opens a lead deal.
				dealId: dealIdToLink || undefined,
				meetingNotes: meetingNotes || undefined
			});
			const hiringRequestId = response.data.id;

			// Upload context files (if any)
			const pendingFiles = contextFiles.filter((f: any) => f._file);
			for (const f of pendingFiles) {
				try {
					await hiringApi.uploadContextFile(hiringRequestId, (f as any)._file);
				} catch (e) {
					console.warn('Failed to upload context file:', (f as any).name, e);
				}
			}

			// Use single orchestrated endpoint: criteria completes first, then modules are queued
			if (selectedModules.length > 0) {
				try {
					await hiringApi.generateModules(hiringRequestId, selectedModules, true, aiContext || undefined, undefined, undefined, meetingNotes || undefined);
				} catch (e) {
					console.warn('Failed to generate modules:', e);
				}
			}

			return response;
		},
		onSuccess: (response) => {
			queryClient.invalidateQueries({ queryKey: ['hiring-requests'] });
			const description = selectedModules.length > 0
				? `Hiring request created. Criteria + ${selectedModules.length} modules queued for generation.`
				: 'Hiring request created successfully';
			toast({ title: 'Created', description });
			// Reset state
			setCreationStep('basic');
			setSelectedModules(ALL_MODULES.map(m => m.type));
			setAiContext(''); setMeetingNotes(''); setContextFiles([]); setDealIdToLink(null); navigate(`/hiring-requests/${response.data.id}`);
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => hiringApi.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['hiring-requests'] });
			toast({ title: 'Deleted', description: 'Hiring request deleted' });
		}
	});

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		if (creationStep === 'basic') {
			// Validate PIC — at least 1 required when company is selected
			if (newRequest.companyId && newRequest.pic.length === 0) {
				toast({ title: 'PIC Required', description: 'Please add at least one Person in Charge (PIC) for portal verification.', variant: 'destructive' });
				return;
			}
			// Move to module selection step
			setCreationStep('modules');
		} else {
			// Validate meeting notes required
			if (!meetingNotes.trim()) {
				toast({ title: 'Meeting Notes Required', description: 'Please provide meeting notes from the Deal Briefing Session 1.', variant: 'destructive' });
				return;
			}
			// Submit the form
			createMutation.mutate(newRequest);
		}
	};

	const handleToggleModule = useCallback((moduleType: ModuleType) => {
		setSelectedModules((prev) => {
			if (prev.includes(moduleType)) {
				// Unchecking: also uncheck all dependants
				const toRemove = new Set<ModuleType>([moduleType, ...(MODULE_DEPENDANTS[moduleType] || [])]);
				return prev.filter((m) => !toRemove.has(m));
			} else {
				// Checking: also check all dependencies
				const toAdd = new Set<ModuleType>([moduleType, ...(MODULE_DEPENDENCIES[moduleType] || [])]);
				return [...new Set([...prev, ...toAdd])];
			}
		});
	}, []);

	// A module is disabled if any of its dependencies are not selected
	const isModuleDisabled = useCallback((moduleType: ModuleType) => {
		const deps = MODULE_DEPENDENCIES[moduleType] || [];
		return deps.some((dep) => !selectedModules.includes(dep));
	}, [selectedModules]);

	const handleCancel = () => {
		setIsCreating(false);
		setCreationStep('basic');
		setSelectedModules(ALL_MODULES.map(m => m.type));
		setAiContext('');
		setMeetingNotes('');
		setContextFiles([]);
		setNewRequest({
			roleTitle: '', yearsOfExperience: 'below_2_years', directReports: 'individual_contributor',
			salaryRange: null, languages: [], industry: [], graduates: '', openToExpat: false,
			excludedCompanies: [], preferredCompanies: [], hiringManagerEmails: [] as Array<{ name: string; position?: string; linkedIn?: string }>, pic: [],
			workType: 'hybrid', contractType: '', urgencyLevel: '', serviceType: '', priority: '', feePercentage: '', dealNotes: '', targetDate: '', companyId: '', location: ''
		});
	};

	// Search + creator filtering now happens server-side, so the returned page is the result set.
	const requests = hiringData?.data || [];

	// Creators for the filter dropdown come from the dedicated endpoint (covers all pages).
	const creators = creatorsData?.data || [];

	const getStatusBadge = (status: string) => {
		const styles: Record<string, string> = {
			draft: 'bg-gray-100 text-gray-700',
			gathering_criteria: 'bg-blue-100 text-blue-700',
			criteria_complete: 'bg-indigo-100 text-indigo-700',
			jd_generated: 'bg-orange-100 text-orange-700',
			pending_feedback: 'bg-purple-100 text-purple-700',
			hiring: 'bg-teal-100 text-teal-700',
			completed: 'bg-green-100 text-green-700',
			cancelled: 'bg-red-100 text-red-700'
		};
		const labels: Record<string, string> = {
			draft: 'Draft',
			gathering_criteria: 'Gathering Criteria',
			criteria_complete: 'Ready for Sourcing',
			jd_generated: 'JD Generated',
			pending_feedback: 'Pending Feedback',
			hiring: 'Hiring',
			completed: 'Completed',
			cancelled: 'Cancelled'
		};
		return { style: styles[status] || styles.draft, label: labels[status] || status.replace(/_/g, ' ') };
	};

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
				<div>
					<h1 className="text-2xl font-bold">Hiring Requests</h1>
					<p className="text-muted-foreground">Manage your hiring requests and criteria</p>
				</div>
				<div className="flex items-center gap-2">
					<Select value={dealStatusFilter} onValueChange={setDealStatusFilter}>
						<SelectTrigger className="w-[150px]">
							<SelectValue placeholder="All Pipeline" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Pipeline</SelectItem>
							{DEAL_STAGE_OPTIONS.map((o) => (
								<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select value={creatorFilter} onValueChange={setCreatorFilter}>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="All Creators" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Creators</SelectItem>
							{creators.map((c: any) => (
								<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button onClick={() => setIsCreating(true)}>
						<Plus className="mr-2 h-4 w-4" />
						New Request
					</Button>
				</div>
			</div>

			{/* Create Form */}
			{isCreating && (
				<Card className="mb-6">
					<CardHeader>
						<CardTitle>
							{creationStep === 'basic' ? 'New Hiring Request' : 'Module Generation'}
						</CardTitle>
						<CardDescription>
							{creationStep === 'basic'
								? 'Enter basic information about the role'
								: 'Choose how you want to generate content for this request'}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleCreate}>
							{creationStep === 'basic' ? (
								<>
									<div className="grid gap-4 md:grid-cols-4">
										<div>
											<label className="text-sm font-medium mb-2 block">Role Title</label>
											<Input
												placeholder="e.g. Senior Backend Engineer"
												value={newRequest.roleTitle}
												onChange={(e) => setNewRequest({ ...newRequest, roleTitle: e.target.value })}
												required
											/>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Years of Experience</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.yearsOfExperience}
												onChange={(e) => setNewRequest({ ...newRequest, yearsOfExperience: e.target.value })}
											>
												{Object.entries(YEARS_OF_EXPERIENCE_LABELS).map(([value, label]) => (
													<option key={value} value={value}>{label}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Direct Reports</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.directReports}
												onChange={(e) => setNewRequest({ ...newRequest, directReports: e.target.value })}
											>
												{Object.entries(DIRECT_REPORTS_LABELS).map(([value, label]) => (
													<option key={value} value={value}>{label}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Work Type</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.workType}
												onChange={(e) => setNewRequest({ ...newRequest, workType: e.target.value })}
											>
												{Object.values(WORK_TYPES).map(wt => (
													<option key={wt} value={wt}>{wt.charAt(0).toUpperCase() + wt.slice(1)}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Contract Type</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.contractType}
												onChange={(e) => setNewRequest({ ...newRequest, contractType: e.target.value })}
											>
												<option value="">Select...</option>
												{Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
													<option key={value} value={value}>{label as string}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Urgency</label>
											<input
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.urgencyLevel}
												onChange={(e) => setNewRequest({ ...newRequest, urgencyLevel: e.target.value })}
												placeholder="e.g. Maximum Join Date August/September (2-3 months notice period acceptable)"
											/>
										</div>
									</div>

									{/* Deal-parity fields — optional at creation, can be completed later. */}
									<div className="grid gap-4 md:grid-cols-4 mt-4">
										<div>
											<label className="text-sm font-medium mb-2 block">Service Type</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.serviceType}
												onChange={(e) => setNewRequest({ ...newRequest, serviceType: e.target.value })}
											>
												<option value="">Select...</option>
												{SERVICE_TYPE_OPTIONS.map((o) => (
													<option key={o.value} value={o.value}>{o.label}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Priority</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.priority}
												onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value })}
											>
												<option value="">Select...</option>
												{PRIORITY_OPTIONS.map((o) => (
													<option key={o.value} value={o.value}>{o.label}</option>
												))}
											</select>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Fee %</label>
											<input
												type="number"
												min={0}
												max={100}
												step="0.1"
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.feePercentage}
												onChange={(e) => setNewRequest({ ...newRequest, feePercentage: e.target.value })}
												placeholder="e.g. 20"
											/>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Target Start Date</label>
											<input
												type="date"
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.targetDate}
												onChange={(e) => setNewRequest({ ...newRequest, targetDate: e.target.value })}
											/>
										</div>
									</div>

									<div className="mt-4">
										<label className="text-sm font-medium mb-2 block">Deal Description</label>
										<Textarea
											rows={3}
											placeholder="Context about this opportunity…"
											value={newRequest.dealNotes}
											onChange={(e) => setNewRequest({ ...newRequest, dealNotes: e.target.value })}
										/>
									</div>

									<div className="grid gap-4 md:grid-cols-4 mt-4">
										<div>
											<label className="text-sm font-medium mb-2 block">Company</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.companyId}
												onChange={(e) => setNewRequest({ ...newRequest, companyId: e.target.value, pic: [] })}
											>
												<option value="">Select company...</option>
												{companiesData?.data
													?.filter((c: any) => c.research?.status === 'completed')
													.map((c: any) => (
														<option key={c.id} value={c.id}>{c.name}</option>
													))}
											</select>
											{companiesData?.data?.some((c: any) => c.research?.status !== 'completed') && (
												<p className="text-xs text-muted-foreground mt-1">
													Only companies with completed research are shown.
												</p>
											)}
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Location</label>
											<LocationSelect
												value={newRequest.location}
												onChange={(val) => setNewRequest({ ...newRequest, location: val })}
												required
											/>
										</div>
										<div>
											<label className="text-sm font-medium mb-2 block">Graduates</label>
											<select
												className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
												value={newRequest.graduates}
												onChange={(e) => setNewRequest({ ...newRequest, graduates: e.target.value })}
											>
												<option value="">Not specified</option>
												{Object.entries(GRADUATE_TYPES_LABELS).map(([value, label]) => (
													<option key={value} value={value}>{label}</option>
												))}
											</select>
										</div>
										<div className="flex items-end">
											<label className="flex items-center gap-2 h-9">
												<input
													type="checkbox"
													checked={newRequest.openToExpat}
													onChange={(e) => setNewRequest({ ...newRequest, openToExpat: e.target.checked })}
													className="rounded"
												/>
												<span className="text-sm font-medium">Open to Expat</span>
											</label>
										</div>
									</div>

									{/* Salary Range */}
									<div className="mt-4">
										<label className="text-sm font-medium mb-2 block">Salary Range (Nett)</label>
										<div className="grid gap-2 md:grid-cols-3">
											<Input
												placeholder="Min"
												value={formatNumber(newRequest.salaryRange?.min)}
												onChange={(e) => {
													const val = parseFormattedNumber(e.target.value);
													setNewRequest({
														...newRequest,
														salaryRange: { min: val, max: newRequest.salaryRange?.max ?? 0, currency: newRequest.salaryRange?.currency ?? 'IDR' }
													});
												}}
											/>
											<Input
												placeholder="Max"
												value={formatNumber(newRequest.salaryRange?.max)}
												onChange={(e) => {
													const val = parseFormattedNumber(e.target.value);
													setNewRequest({
														...newRequest,
														salaryRange: { min: newRequest.salaryRange?.min ?? 0, max: val, currency: newRequest.salaryRange?.currency ?? 'IDR' }
													});
												}}
											/>
											<Input
												placeholder="Currency (e.g. IDR)"
												value={newRequest.salaryRange?.currency ?? ''}
												onChange={(e) => setNewRequest({
													...newRequest,
													salaryRange: { min: newRequest.salaryRange?.min ?? 0, max: newRequest.salaryRange?.max ?? 0, currency: e.target.value }
												})}
											/>
										</div>
									</div>

									{/* Languages */}
									<div className="mt-4">
										<label className="text-sm font-medium mb-2 block">Languages</label>
										<div className="flex flex-wrap gap-1 mb-2">
											{newRequest.languages.map((lang, i) => (
												<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
													{lang}
													<button type="button" onClick={() => setNewRequest({ ...newRequest, languages: newRequest.languages.filter((_, idx) => idx !== i) })}>
														<X className="h-3 w-3" />
													</button>
												</span>
											))}
										</div>
										<div className="flex gap-2">
											<select
												className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm flex-1"
												value={languageInput}
												onChange={(e) => {
													if (e.target.value && !newRequest.languages.includes(e.target.value)) {
														setNewRequest({ ...newRequest, languages: [...newRequest.languages, e.target.value] });
													}
													setLanguageInput('');
												}}
											>
												<option value="">Select language...</option>
												{LANGUAGES.filter(l => !newRequest.languages.includes(l)).map(l => (
													<option key={l} value={l}>{l}</option>
												))}
											</select>
											<Input
												placeholder="Custom language"
												className="flex-1"
												value={languageInput}
												onChange={(e) => setLanguageInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' && languageInput.trim() && !newRequest.languages.includes(languageInput.trim())) {
														e.preventDefault();
														setNewRequest({ ...newRequest, languages: [...newRequest.languages, languageInput.trim()] });
														setLanguageInput('');
													}
												}}
											/>
										</div>
									</div>

									{/* Industry */}
									<div className="mt-4">
										<label className="text-sm font-medium mb-2 block">Industry</label>
										<div className="flex flex-wrap gap-1 mb-2">
											{newRequest.industry.map((ind, i) => (
												<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
													{ind}
													<button type="button" onClick={() => setNewRequest({ ...newRequest, industry: newRequest.industry.filter((_, idx) => idx !== i) })}>
														<X className="h-3 w-3" />
													</button>
												</span>
											))}
										</div>
										<Input
											placeholder="Type industry and press Enter"
											value={industryInput}
											onChange={(e) => setIndustryInput(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === 'Enter' && industryInput.trim() && !newRequest.industry.includes(industryInput.trim())) {
													e.preventDefault();
													setNewRequest({ ...newRequest, industry: [...newRequest.industry, industryInput.trim()] });
													setIndustryInput('');
												}
											}}
										/>
									</div>

									<div className="grid gap-4 md:grid-cols-2 mt-4">
										{/* Excluded Companies */}
										<div>
											<label className="text-sm font-medium mb-2 block">Excluded Companies</label>
											<div className="flex flex-wrap gap-1 mb-2">
												{newRequest.excludedCompanies.map((c, i) => (
													<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
														{c}
														<button type="button" onClick={() => setNewRequest({ ...newRequest, excludedCompanies: newRequest.excludedCompanies.filter((_, idx) => idx !== i) })}>
															<X className="h-3 w-3" />
														</button>
													</span>
												))}
											</div>
											<Input
												placeholder="Type company name and press Enter"
												value={excludedInput}
												onChange={(e) => setExcludedInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' && excludedInput.trim()) {
														e.preventDefault();
														setNewRequest({ ...newRequest, excludedCompanies: [...newRequest.excludedCompanies, excludedInput.trim()] });
														setExcludedInput('');
													}
												}}
											/>
										</div>

										{/* Preferred Companies */}
										<div>
											<label className="text-sm font-medium mb-2 block">Preferred Companies</label>
											<div className="flex flex-wrap gap-1 mb-2">
												{newRequest.preferredCompanies.map((c, i) => (
													<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
														{c}
														<button type="button" onClick={() => setNewRequest({ ...newRequest, preferredCompanies: newRequest.preferredCompanies.filter((_, idx) => idx !== i) })}>
															<X className="h-3 w-3" />
														</button>
													</span>
												))}
											</div>
											<Input
												placeholder="Type company name and press Enter"
												value={preferredInput}
												onChange={(e) => setPreferredInput(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' && preferredInput.trim()) {
														e.preventDefault();
														setNewRequest({ ...newRequest, preferredCompanies: [...newRequest.preferredCompanies, preferredInput.trim()] });
														setPreferredInput('');
													}
												}}
											/>
										</div>
									</div>

									{/* Hiring Managers */}
									<div className="mt-4">
										<label className="text-sm font-medium mb-2 block">Hiring Managers</label>
										<div className="space-y-2 mb-2">
											{newRequest.hiringManagerEmails.map((hm, i) => (
												<div key={i} className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-1.5">
													<span className="text-sm flex-1">
														{hm.name}{hm.position ? ` — ${hm.position}` : ''}{hm.linkedIn ? ` • ${hm.linkedIn}` : ''}
													</span>
													<button type="button" onClick={() => setNewRequest({ ...newRequest, hiringManagerEmails: newRequest.hiringManagerEmails.filter((_, idx) => idx !== i) })}>
														<X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
													</button>
												</div>
											))}
										</div>
										<div className="flex gap-2">
											<Input
												placeholder="Name *"
												value={hmNameInput}
												onChange={(e) => setHmNameInput(e.target.value)}
												className="flex-1"
											/>
											<Input
												placeholder="Position (optional)"
												value={hmPositionInput}
												onChange={(e) => setHmPositionInput(e.target.value)}
												className="flex-1"
											/>
											<Input
												placeholder="LinkedIn URL (optional)"
												value={hmLinkedInInput}
												onChange={(e) => setHmLinkedInInput(e.target.value)}
												className="flex-1"
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault();
														const name = hmNameInput.trim();
														if (!name) return;
														setNewRequest({ ...newRequest, hiringManagerEmails: [...newRequest.hiringManagerEmails, { name, position: hmPositionInput.trim() || undefined, linkedIn: hmLinkedInInput.trim() || undefined }] });
														setHmNameInput('');
														setHmPositionInput('');
														setHmLinkedInInput('');
													}
												}}
											/>
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => {
													const name = hmNameInput.trim();
													if (!name) return;
													setNewRequest({ ...newRequest, hiringManagerEmails: [...newRequest.hiringManagerEmails, { name, position: hmPositionInput.trim() || undefined, linkedIn: hmLinkedInInput.trim() || undefined }] });
													setHmNameInput('');
													setHmPositionInput('');
													setHmLinkedInInput('');
												}}
											>
												Add
											</Button>
										</div>
										<p className="text-xs text-muted-foreground mt-1">Name is required. Press Enter or click Add.</p>
									</div>

									{/* PIC */}
									{newRequest.companyId && (
										<div className="mt-4">
											<label className="text-sm font-medium mb-2 block">
												PIC (Person in Charge) <span className="text-destructive">*</span>
												<span className="text-xs text-muted-foreground ml-1">— at least 1 required for portal verification</span>
											</label>
											<div className="flex flex-wrap gap-1 mb-2">
												{newRequest.pic.map((p, i) => (
													<span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
														{p.name} ({p.email})
														<button type="button" onClick={() => setNewRequest({ ...newRequest, pic: newRequest.pic.filter((_, idx) => idx !== i) })}>
															<X className="h-3 w-3" />
														</button>
													</span>
												))}
											</div>
											{/* Select from existing contacts */}
											{contactsData?.data && contactsData.data.length > 0 && (
												<select
													className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm mb-2"
													value=""
													onChange={(e) => {
														const contact = contactsData.data.find((c: any) => c.id === e.target.value);
														if (contact && !newRequest.pic.some(p => p.contactId === contact.id)) {
															setNewRequest({ ...newRequest, pic: [...newRequest.pic, { contactId: contact.id, name: contact.name, email: contact.email }] });
														}
													}}
												>
													<option value="">Select existing contact...</option>
													{contactsData.data.filter((c: any) => !newRequest.pic.some(p => p.contactId === c.id)).map((c: any) => (
														<option key={c.id} value={c.id}>{c.name} ({c.email})</option>
													))}
												</select>
											)}
											{/* Or add custom PIC */}
											<div className="grid gap-2 md:grid-cols-3">
												<Input placeholder="Name" value={picInput.name} onChange={(e) => setPicInput({ ...picInput, name: e.target.value })} />
												<Input placeholder="Email" value={picInput.email} onChange={(e) => setPicInput({ ...picInput, email: e.target.value })} />
												<Button type="button" variant="outline" size="sm" onClick={() => {
													if (picInput.name && picInput.email && picInput.email.includes('@')) {
														setNewRequest({ ...newRequest, pic: [...newRequest.pic, { name: picInput.name, email: picInput.email }] });
														setPicInput({ name: '', email: '' });
													}
												}}>Add PIC</Button>
											</div>
										</div>
									)}

									<div className="flex gap-2 mt-4">
										<Button type="submit" disabled={!newRequest.roleTitle || !newRequest.companyId}>
											Next
										</Button>
										<Button type="button" variant="outline" onClick={handleCancel}>
											Cancel
										</Button>
									</div>
								</>
							) : (
								<>
									{/* Module Selection */}
									<div className="space-y-4">
										<p className="text-sm text-muted-foreground">Choose which modules to generate for this request</p>

										<div className="p-4 border rounded-lg bg-muted/30">
											{/* Hiring Criteria - always selected and disabled */}
											<label
												className="flex items-center gap-2 p-2 rounded transition-colors mb-3 border bg-primary/10 text-primary border-primary/30 opacity-80 cursor-not-allowed"
											>
												<input
													type="checkbox"
													checked={true}
													disabled
													className="rounded"
												/>
												<div>
													<span className="text-sm font-medium">Hiring Criteria</span>
													<span className="text-xs text-muted-foreground ml-2">(always generated first — foundation for all modules)</span>
												</div>
											</label>

											<p className="text-sm font-medium mb-3">Select modules to generate:</p>
											<div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
												{ALL_MODULES.map((mod) => {
													const disabled = isModuleDisabled(mod.type);
													const checked = selectedModules.includes(mod.type);
													const deps = MODULE_DEPENDENCIES[mod.type] || [];
													const missingDeps = deps.filter(d => !selectedModules.includes(d));
													return (
														<label
															key={mod.type}
															title={disabled ? `Requires: ${missingDeps.map(d => ALL_MODULES.find(m => m.type === d)?.label).join(', ')}` : mod.description}
															className={`flex items-center gap-2 p-2 rounded transition-colors ${disabled
																	? 'opacity-40 cursor-not-allowed'
																	: checked
																		? 'bg-primary/10 text-primary cursor-pointer'
																		: 'hover:bg-muted cursor-pointer'
																}`}
														>
															<input
																type="checkbox"
																checked={checked}
																disabled={disabled}
																onChange={() => handleToggleModule(mod.type)}
																className="rounded"
															/>
															<span className="text-sm">{mod.label}</span>
														</label>
													);
												})}
											</div>
											{selectedModules.length > 0 && (
												<p className="text-xs text-muted-foreground mt-2">
													{selectedModules.length} module(s) selected for generation
												</p>
											)}
										</div>
									</div>

									{/* Meeting Notes (required — feeds Deal Briefing Section 1) */}
									<div className="mt-4">
										<div className="flex items-center gap-2 mb-2">
											<MessageSquare className="h-4 w-4 text-primary" />
											<label className="text-sm font-medium">Meeting Notes</label>
											<span className="text-xs text-destructive">*required</span>
										</div>
										<p className="text-xs text-muted-foreground mb-2">Raw notes from the Deal Briefing Session 1 intake meeting. This will be used to generate the Deal Briefing module.</p>
										<Textarea
											placeholder="Paste your raw meeting notes from the intake/briefing session here..."
											value={meetingNotes}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMeetingNotes(e.target.value)}
											rows={5}
											className="text-sm"
											required
										/>
									</div>

									{/* Additional Context */}
									<div className="mt-4">
										<div className="flex items-center gap-2 mb-2">
											<MessageSquare className="h-4 w-4 text-primary" />
											<label className="text-sm font-medium">Additional Context</label>
											<span className="text-xs text-muted-foreground">(optional)</span>
										</div>
										<Textarea
											placeholder="e.g. 'Focus on fintech companies', 'Add more unconventional searches targeting Go developers', 'Prioritize candidates with startup experience'..."
											value={aiContext}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAiContext(e.target.value)}
											rows={3}
											className="text-sm"
										/>
										{/* Context File Upload */}
										<div className="mt-3">
											<p className="text-xs text-muted-foreground mb-2">Upload supporting documents (PDF, DOC, DOCX, TXT — max 10MB each)</p>
											{contextFiles.length > 0 && (
												<div className="space-y-1 mb-2">
													{contextFiles.map((f, i) => (
														<div key={i} className="flex items-center gap-2 px-2 py-1 bg-muted/50 rounded text-xs">
															<FileText className="h-3 w-3 flex-shrink-0" />
															<span className="flex-1 truncate">{f.name}</span>
															<span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
															<button type="button" onClick={() => setContextFiles(contextFiles.filter((_, idx) => idx !== i))}>
																<X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
															</button>
														</div>
													))}
												</div>
											)}
											<label className="inline-flex items-center gap-2 px-3 py-1.5 border border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
												<Upload className="h-3.5 w-3.5" />
												<span className="text-xs font-medium">{isUploadingFile ? 'Uploading...' : 'Upload File'}</span>
												<input
													type="file"
													className="hidden"
													accept=".pdf,.doc,.docx,.txt"
													disabled={isUploadingFile}
													onChange={async (e) => {
														const file = e.target.files?.[0];
														if (!file) return;
														if (file.size > 10 * 1024 * 1024) {
															toast({ title: 'Error', description: 'File must be under 10MB', variant: 'destructive' });
															return;
														}
														setContextFiles(prev => [...prev, {
															url: '',
															gcsPath: '',
															name: file.name,
															size: file.size,
															uploadedAt: new Date().toISOString(),
															_file: file
														} as any]);
														e.target.value = '';
													}}
												/>
											</label>
										</div>
									</div>

									<div className="flex gap-2 mt-6">
										<Button
											type="button"
											variant="outline"
											onClick={() => setCreationStep('basic')}
										>
											Back
										</Button>
										<Button
											type="submit"
											disabled={createMutation.isPending}
										>
											{createMutation.isPending ? 'Creating...' : 'Create Request'}
										</Button>
										<Button type="button" variant="ghost" onClick={handleCancel}>
											Cancel
										</Button>
									</div>
								</>
							)}
						</form>
					</CardContent>
				</Card >
			)
			}

			{/* Search */}
			<div className="relative mb-6">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder="Search by role or company..."
					className="pl-10"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>
			</div>

			{/* List */}
			{
				isLoading ? (
					<div className="text-center py-12">Loading...</div>
				) : requests.length === 0 ? (
					<Card>
						<CardContent className="py-12 text-center">
							<p className="text-muted-foreground">No hiring requests found</p>
						</CardContent>
					</Card>
				) : (
					<div className="space-y-3">
						{requests.map((request: any) => (
							<Link
								key={request.id}
								to={`/hiring-requests/${request.id}`}
								className="block p-4 rounded-lg border bg-card hover:bg-accent transition-colors"
							>
								<div className="flex items-center justify-between">
									<div className="flex-1 min-w-0">
										<h3 className="font-medium">{request.roleTitle}</h3>
										<p className="text-sm text-muted-foreground mt-1">
											{request.company?.name || 'No company'} • {YEARS_OF_EXPERIENCE_LABELS[request.yearsOfExperience] || request.yearsOfExperience}
										</p>
										<div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
											<span>
												Creator: {request.user?.name || 'Unknown'}
											</span>
											{request.sharedUsers?.length > 0 && (
												<span>
													Invited: {request.sharedUsers.join(', ')}
												</span>
											)}
										</div>
									</div>
									<div className="flex items-center gap-3">
										<div className="flex flex-col items-end gap-1">
											<span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(request.status).style}`}>
												{getStatusBadge(request.status).label}
											</span>
											<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
												{DEAL_STAGE_OPTIONS.find((o) => o.value === request.dealStatus)?.label || 'Lead'}
											</span>
											<span className="text-xs text-muted-foreground">
												Last Update : {new Date(request.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
											</span>
										</div>
										<Button
											variant="ghost"
											size="icon"
											title="Share"
											onClick={(e) => {
												e.preventDefault();
												setShareTargetRequest(request);
												setShareDialogOpen(true);
											}}
										>
											<Users className="h-4 w-4 text-muted-foreground" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.preventDefault();
												if (window.confirm('Delete this hiring request?')) {
													deleteMutation.mutate(request.id);
												}
											}}
										>
											<Trash2 className="h-4 w-4 text-muted-foreground" />
										</Button>
									</div>
								</div>
							</Link>
						))}
					</div>
				)
			}

			{/* Pagination */}
			{hiringData?.meta && hiringData.meta.totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-8">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setPage(p => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						Previous
					</Button>
					<span className="text-sm text-muted-foreground">
						Page {page} of {hiringData.meta.totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setPage(p => p + 1)}
						disabled={page >= hiringData.meta.totalPages}
					>
						Next
					</Button>
				</div>
			)}

			{/* Share Dialog */}
			{shareTargetRequest && (
				<ShareDialog
					open={shareDialogOpen}
					onOpenChange={(open) => {
						setShareDialogOpen(open);
						if (!open) setShareTargetRequest(null);
					}}
					hiringRequestId={shareTargetRequest.id}
					currentSharedTo={shareTargetRequest.sharedTo || []}
					currentUserId={currentUser?.id || ''}
					onSuccess={() => {
						queryClient.invalidateQueries({ queryKey: ['hiring-requests'] });
					}}
				/>
			)}
		</div >
	);
}
